import { useUser } from '@clerk/react-router';
import { useEffect, useRef } from 'react';
import { UserContext } from '~/app';
import type { ProfileMachineRef } from './types';

/**
 * [MCV-1834] Keeps the Clerk name in step with the MCV profile: when the signed-in provider saves
 * a changed first or last name on the Personal tab of their own profile, the same name is written
 * to their Clerk user (the sign-in surfaces, emails and the public header read it).
 *
 * Never for a delegated employer editing a provider's vault (the Clerk user is the employer's),
 * and never unless the saved provider belongs to the signed-in Clerk user. A failed Clerk update
 * is logged and ignored: the profile save has already succeeded. The localStorage name hint is
 * not touched here; `ProviderNameHintSync` rewrites it from the vault's provider.
 *
 * Updates run one at a time in save order, so two quick saves can't finish out of order and
 * leave Clerk with the earlier name.
 */
export const useSyncClerkNameOnProfileSave = (profileRef: ProfileMachineRef | undefined) => {
  const { user } = useUser();
  const pendingUpdate = useRef<Promise<void>>(Promise.resolve());
  const isDelegating = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );

  useEffect(() => {
    if (!profileRef || !user || isDelegating) return;

    const subscription = profileRef.on('PROFILE_NAME_SAVED', (saved) => {
      if (!saved.clerkUserId || saved.clerkUserId !== user.id) return;
      const firstName = saved.firstName.trim();
      const lastName = saved.lastName.trim();
      if (!firstName) return;
      const changed =
        firstName !== saved.previousFirstName.trim() || lastName !== saved.previousLastName.trim();
      if (!changed) return;
      if ((user.firstName ?? '') === firstName && (user.lastName ?? '') === lastName) return;

      pendingUpdate.current = pendingUpdate.current.then(async () => {
        try {
          await user.update({ firstName, lastName });
        } catch (error) {
          console.error('[SERVICE] Failed to copy the profile name to the Clerk user:', error);
        }
      });
    });

    return () => subscription.unsubscribe();
  }, [profileRef, user, isDelegating]);
};
